import { hasPermission } from "../entities/access/permissions";
import type { ModuleNavigationItem } from "../entities/modules";
import type { IconType } from "react-icons";
import {
  FiActivity,
  FiBell,
  FiBriefcase,
  FiCheckCircle,
  FiFileText,
  FiFolder,
  FiGitBranch,
  FiGrid,
  FiEdit3,
  FiMail,
  FiShield,
  FiUser,
  FiUsers,
} from "react-icons/fi";

export interface WorkspaceNavigationItem {
  key: string;
  path: string;
  label: string;
  description: string;
  icon: IconType;
}

interface WorkspaceNavigationOptions {
  permissions: readonly string[];
  modules: readonly ModuleNavigationItem[];
}

interface PermissionNavigationItem extends WorkspaceNavigationItem {
  /** Одно право или список: раздел виден при любом из них. */
  permission: string | readonly string[];
}

function hasAnyPermission(
  permissions: readonly string[],
  required: string | readonly string[],
): boolean {
  return (typeof required === "string" ? [required] : required).some(
    (permission) => hasPermission({ permissions }, permission),
  );
}

const siteContentEntry: PermissionNavigationItem = {
  key: "site-content",
  path: "/site",
  label: "Публичный сайт",
  description: "Новости, страницы, медиа и настройки сайта",
  icon: FiEdit3,
  permission: "site.access",
};

const siteContentItems: readonly PermissionNavigationItem[] = [
  {
    key: "site-news",
    path: "/site/news",
    label: "Новости сайта",
    description: "Черновики, публикации, preview и расписание",
    icon: FiEdit3,
    permission: "site.news.view",
  },
  {
    key: "site-categories",
    path: "/site/categories",
    label: "Категории новостей",
    description: "Рубрики публикаций и SEO",
    icon: FiFolder,
    permission: "site.categories.manage",
  },
  {
    key: "site-pages",
    path: "/site/pages",
    label: "Страницы сайта",
    description: "Статические страницы и публикация",
    icon: FiFileText,
    permission: "site.pages.manage",
  },
  {
    key: "site-slides",
    path: "/site/slides",
    label: "Слайды главной",
    description: "Главный экран публичного сайта",
    icon: FiGrid,
    permission: "site.settings.manage",
  },
  {
    key: "site-settings",
    path: "/site/settings",
    label: "Настройки сайта",
    description: "Контакты и SEO по умолчанию",
    icon: FiGrid,
    permission: "site.settings.manage",
  },
];

// Справочник контрагентов относится к модулю документооборота: он появляется
// в меню вместе с модулем и не показывается, если модуль отключён.
export const counterpartiesPath = "/modules/document-workflow/counterparties";

const counterpartiesEntry: WorkspaceNavigationItem = {
  key: "counterparties",
  path: counterpartiesPath,
  label: "Контрагенты",
  description:
    "Реквизиты, контакты, правила определения отправителя и разбор писем",
  icon: FiBriefcase,
};

/** Разделы страницы «Пользователи»: карточки сотрудников и дерево сотрудников. */
const userSectionItems: readonly PermissionNavigationItem[] = [
  {
    key: "users-list",
    path: "/administration/users/list",
    label: "Сотрудники",
    description: "Карточки, доступ, безопасность и жизненный цикл",
    icon: FiUser,
    permission: "users.view",
  },
  {
    key: "users-hierarchy",
    path: "/administration/users/hierarchy",
    label: "Иерархия",
    description: "Дерево иерархии сотрудников",
    icon: FiGitBranch,
    permission: "users.hierarchy.view",
  },
];

const approvalSectionItems: readonly PermissionNavigationItem[] = [
  {
    key: "approval-tasks",
    path: "/modules/document-workflow/approvals/tasks",
    label: "Мне на согласование",
    description: "Личные задания согласования",
    icon: FiFileText,
    permission: "document-workflow.approvals.view",
  },
  {
    key: "approval-templates",
    path: "/modules/document-workflow/approvals/templates",
    label: "Шаблоны согласования",
    description: "Маршруты согласования документов",
    icon: FiGitBranch,
    permission: "document-workflow.approvals.view",
  },
];

const administrationItems: readonly PermissionNavigationItem[] = [
  siteContentEntry,
  {
    key: "users",
    path: "/administration/users",
    label: "Пользователи",
    description: "Карточки сотрудников и дерево сотрудников",
    icon: FiUsers,
    permission: ["users.view", "users.hierarchy.view"],
  },
  {
    key: "organization",
    path: "/administration/scopes",
    label: "КПО",
    description: "Структура компании, подразделения и доступные контексты",
    icon: FiGitBranch,
    permission: "organization.view",
  },
  {
    key: "roles",
    path: "/administration/roles",
    label: "Роли и права",
    description: "Роли, права и управление доступом сотрудников",
    icon: FiShield,
    permission: "roles.view",
  },
  {
    key: "audit",
    path: "/administration/audit",
    label: "Журнал аудита",
    description: "История критичных действий и событий соответствия",
    icon: FiActivity,
    permission: "audit.view",
  },
  {
    key: "mailboxes",
    path: "/administration/mailboxes",
    label: "Почтовые ящики",
    description: "Настройки ящиков, синхронизация и ошибки импорта писем",
    icon: FiMail,
    permission: [
      "document-workflow.mailboxes.manage",
      "document-workflow.failures.manage",
    ],
  },
  {
    key: "files",
    path: "/administration/files",
    label: "Файлы",
    description: "Загрузка, просмотр и управление файлами",
    icon: FiFolder,
    permission: "files.upload",
  },
  {
    key: "modules",
    path: "/administration/modules",
    label: "Модули",
    description: "Подключённые модули и их настройки",
    icon: FiGrid,
    permission: "modules.view",
  },
  {
    key: "operations",
    path: "/administration/operations",
    label: "Состояние системы",
    description: "Готовность сервисов, очереди и операции",
    icon: FiActivity,
    permission: "modules.configure",
  },
  {
    key: "notifications",
    path: "/notifications",
    label: "Уведомления",
    description: "Входящие системные уведомления и настройки",
    icon: FiBell,
    permission: "notifications.view",
  },
];

export function workspaceNavigation({
  permissions,
  modules,
}: WorkspaceNavigationOptions): WorkspaceNavigationItem[] {
  const administration = administrationItems.filter((item) =>
    hasAnyPermission(permissions, item.permission),
  );
  const availableModules = modules.filter(
    (item) =>
      item.permission === undefined ||
      hasPermission({ permissions }, item.permission),
  );
  const toNavigationItem = (
    item: ModuleNavigationItem,
  ): WorkspaceNavigationItem => ({
    key: `module:${item.module_key}:${item.path}`,
    path: item.path,
    label: item.label,
    description: `Модуль ERP: ${item.module_key}`,
    icon: FiFileText,
  });
  const documentWorkflowItems = availableModules
    .filter((item) => item.module_key === "document-workflow")
    .map(toNavigationItem);
  const otherModuleItems = availableModules
    .filter((item) => item.module_key !== "document-workflow")
    .map(toNavigationItem);

  const hasDocumentWorkflow = modules.some(
    (item) =>
      item.module_key === "document-workflow" &&
      (item.permission === undefined ||
        hasPermission({ permissions }, item.permission)) &&
      hasPermission({ permissions }, "document-workflow.view"),
  );

  // Рабочие модули идут первыми, так как ими пользуются чаще всего:
  // документооборот, остальные модули, контрагенты, затем администрирование.
  return [
    ...documentWorkflowItems,
    ...otherModuleItems,
    ...(hasPermission({ permissions }, "document-workflow.approvals.view")
      ? [
          {
            key: "approvals",
            path: "/modules/document-workflow/approvals",
            label: "Согласования",
            description: "Личные задания и шаблоны согласования",
            icon: FiCheckCircle,
          },
        ]
      : []),
    ...(hasDocumentWorkflow ? [counterpartiesEntry] : []),
    ...administration,
  ];
}

export function userSectionsNavigation(
  permissions: readonly string[],
): WorkspaceNavigationItem[] {
  return userSectionItems.filter((item) =>
    hasAnyPermission(permissions, item.permission),
  );
}

export function approvalSectionsNavigation(
  permissions: readonly string[],
): WorkspaceNavigationItem[] {
  return approvalSectionItems.filter((item) =>
    hasAnyPermission(permissions, item.permission),
  );
}

export function siteContentNavigation(
  permissions: readonly string[],
): WorkspaceNavigationItem[] {
  return siteContentItems.filter((item) =>
    hasAnyPermission(permissions, item.permission),
  );
}
